import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, CITIES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { TruckCard } from "@/components/TruckCard";
import { MapPin, CalendarDays, Building2, Search, ArrowRight, Zap } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1768314845819-2f015b7f3b1c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxmb29kJTIwdHJ1Y2slMjB3aW5kb3clMjBzZXJ2aW5nfGVufDB8fHx8MTc4NTE4NTQ2Mnww&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const navigate = useNavigate();
  const [trucks, setTrucks] = useState([]);

  useEffect(() => {
    api.get("/trucks", { params: { live: true } }).then((r) => setTrucks(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="Food truck" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1F2937]/90 via-[#1F2937]/70 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 py-24 sm:py-32">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F2635F] text-white text-xs font-bold uppercase tracking-widest">
              <Zap className="w-3.5 h-3.5" /> Join the Founding 100 · Green Bay · Appleton · Oshkosh · Fox Valley
            </span>
            <h1 className="font-display text-5xl sm:text-6xl font-black text-white leading-none tracking-tighter mt-6">
              Moving Food. <span className="text-[#F2635F]">Moving Crowds.</span>
            </h1>
            <p className="text-lg text-white/80 mt-5 leading-relaxed max-w-xl">
              Get discovered. Get booked. Grow smarter. FlavorFlow is the local marketplace connecting food trucks with hungry crowds, events, and corporate gigs across Wisconsin. Free for foodies, forever.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Button onClick={() => navigate("/explore")} className="rounded-full h-12 px-7 text-base font-bold bg-[#0FB5A6] hover:bg-[#0E9E92] text-white active:scale-95 transition-transform" data-testid="hero-explore-btn">
                <MapPin className="w-5 h-5 mr-2" /> Open the Live Map
              </Button>
              <Button onClick={() => navigate("/founding")} variant="outline" className="rounded-full h-12 px-7 text-base font-bold bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="hero-owner-btn">
                I own a food truck <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SEARCH BAR */}
      <section className="max-w-7xl mx-auto px-5 -mt-8 relative z-10">
        <div className="bg-white rounded-2xl gr-shadow-lg border border-teal-100 p-5 flex flex-col sm:flex-row gap-3">
          <div className="flex-1 flex items-center gap-3 px-3">
            <Search className="w-5 h-5 text-slate-400" />
            <span className="text-slate-500">Search trucks, cuisines, or a city near you…</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            {CITIES.map((c) => (
              <button key={c} onClick={() => navigate(`/explore?city=${encodeURIComponent(c)}`)} className="px-4 py-2 rounded-full bg-teal-50 text-[#0FB5A6] text-sm font-bold hover:bg-teal-100 transition-colors" data-testid={`landing-city-${c}`}>
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-5 py-20">
        <div className="grid md:grid-cols-3 gap-6">
          <Feature icon={<MapPin className="w-6 h-6" />} title="Live Map" desc="See exactly where trucks are parked right now, their hours, menus and status." to="/explore" cta="Find trucks" />
          <Feature icon={<CalendarDays className="w-6 h-6" />} title="Event Marketplace" desc="Organizers list fairs & festivals. Truck owners apply and book vendor spots in a click." to="/events" cta="Browse events" />
          <Feature icon={<Building2 className="w-6 h-6" />} title="Corporate Bookings" desc="Companies request a truck for office parties. Premium owners get the leads first." to="/corporate" cta="Request a truck" />
        </div>
      </section>

      {/* LIVE NOW */}
      {trucks.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 pb-20">
          <div className="flex items-end justify-between mb-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600">Serving right now</p>
              <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Trucks live near you</h2>
            </div>
            <Link to="/explore" className="text-[#0FB5A6] font-bold flex items-center gap-1 hover:gap-2 transition-all">See all <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {trucks.map((t, i) => <TruckCard key={t.id} truck={t} index={i} />)}
          </div>
        </section>
      )}

      {/* OWNER CTA */}
      <section className="max-w-7xl mx-auto px-5 pb-24">
        <div className="rounded-3xl bg-[#1F2937] overflow-hidden grid md:grid-cols-2">
          <div className="p-10 sm:p-14">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#F2635F]">For food truck owners</p>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-white tracking-tight mt-3">Be one of the first 100 trucks.</h2>
            <p className="text-white/70 mt-4 leading-relaxed">List free on the live map, then upgrade to unlock unlimited events, top placement, analytics, and corporate leads. Founding 100 members help shape the future of food truck discovery in Wisconsin.</p>
            <Button onClick={() => navigate("/founding")} className="mt-7 rounded-full h-12 px-7 font-bold bg-[#0FB5A6] hover:bg-[#0E9E92] text-white active:scale-95 transition-transform" data-testid="landing-owner-cta">
              Apply to the Founding 100
            </Button>
          </div>
          <div className="relative min-h-[240px]">
            <img src="https://images.unsplash.com/photo-1612687286248-9811801bd618?w=800" alt="Event crowd" className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </div>
      </section>
    </div>
  );
}

const Feature = ({ icon, title, desc, to, cta }) => (
  <Link to={to} className="group bg-white rounded-2xl border border-teal-100 p-8 hover:-translate-y-1 hover:gr-shadow-lg transition-transform">
    <div className="w-12 h-12 rounded-full bg-teal-50 text-[#0FB5A6] flex items-center justify-center group-hover:bg-[#0FB5A6] group-hover:text-white transition-colors">{icon}</div>
    <h3 className="font-display text-xl font-bold mt-5">{title}</h3>
    <p className="text-slate-500 mt-2 leading-relaxed">{desc}</p>
    <span className="inline-flex items-center gap-1 text-[#0FB5A6] font-bold mt-4 group-hover:gap-2 transition-all">{cta} <ArrowRight className="w-4 h-4" /></span>
  </Link>
);
